import React, { useMemo, useRef, useState } from 'react';
import { aracBilgiAPI, caseFileAPI } from '../../api/api';
import { katla } from '../../utils/aracHasar';
import EkspertizBolumu from './EkspertizBolumu';

/*
  Hesabın en üstündeki "Dosyadan ve ruhsattan doldur" kartı.

  - Tam sayfada: föy no ya da müvekkil adıyla dosya aranır. Seçilince
    dosyadaki plaka, kaza tarihi, sigorta bilgileri gelir ve dosyada adında
    "ruhsat" geçen belge (önce "müvekkil ruhsat") okunur.
  - Dosya formunda: dosya zaten belli; "Ruhsattan doldur" tek düğme.
  - Her iki yerde: dosyada ruhsat yoksa fotoğraf/PDF yüklenip okunabilir
    (yüklenen dosya hiçbir yere kaydedilmez).

  Okuma sunucuda yapılır (OCR), 10-30 sn sürebilir.
*/

// Dava türü API'den sayı olarak gelir (utils/constants DAVA_TURU); adlar da kabul edilir.
const HASAR_TURLERI = ['TrafikKazasi', 'MaddiHasar', 'DegerKaybi', 'MaddiHasarDegerKaybi', 'Kasko', 'MaddiHasarImms', 'DegerKaybiImms',
  'AracMahrumiyetBedeli', 'MaddiHasarDegerKaybiAracMahrumiyet', 1, 5, 6, 7, 9, 10, 11, 48, 49];
const alanAl = (d, ad) => d?.[ad] ?? d?.[ad.charAt(0).toLowerCase() + ad.slice(1)];

const DosyaRuhsat = ({ embedded, dosyaId, seciliDosya, paketSecili, onDosyaSec, onDosyaBirak, onRuhsat, onEkspertiz }) => {
  const [dosyalar, setDosyalar] = useState(null);
  const [ara, setAra] = useState('');
  const [listeAcik, setListeAcik] = useState(false);
  const [okuma, setOkuma] = useState(null);
  const [adaylar, setAdaylar] = useState([]);
  const dosyaGirdisi = useRef(null);

  const kimlik = embedded ? dosyaId : seciliDosya?.id;

  const dosyalariGetir = async () => {
    if (dosyalar) return;
    try {
      const l = await caseFileAPI.getAll();
      setDosyalar(Array.isArray(l) ? l : []);
    } catch (e) { setDosyalar([]); }
  };

  const sonuclar = useMemo(() => {
    if (!dosyalar) return [];
    const q = katla(ara);
    const uygun = (d) => {
      if (!q) return true;
      return String(alanAl(d, 'FoyNo') ?? '').includes(ara.trim())
        || katla(alanAl(d, 'Muvekkil')).includes(q)
        || katla(alanAl(d, 'MuvekkilPlaka')).replace(/\s/g, '').includes(q.replace(/\s/g, ''));
    };
    return dosyalar
      .filter(uygun)
      .sort((a, b) => {
        // Trafik / hasar / değer kaybı dosyaları önce, sonra en yeni föy.
        const ha = HASAR_TURLERI.includes(alanAl(a, 'DavaTuru')) ? 1 : 0;
        const hb = HASAR_TURLERI.includes(alanAl(b, 'DavaTuru')) ? 1 : 0;
        return hb - ha || Number(alanAl(b, 'FoyNo') || 0) - Number(alanAl(a, 'FoyNo') || 0);
      })
      .slice(0, 8);
  }, [dosyalar, ara]);

  const sonucuIsle = async (s, kaynak) => {
    setAdaylar(s.adaylar || []);
    if (!s.okundu) {
      setOkuma({ durum: 'hata', mesaj: s.hata || 'Ruhsat okunamadı.', belgeId: s.belgeId, belgeAdi: s.belgeAdi });
      return;
    }
    const { ozet, eksik } = await onRuhsat(s.bilgi);
    setOkuma({ durum: 'tamam', ozet, eksik, belgeId: s.belgeId, belgeAdi: s.belgeAdi || kaynak });
  };

  const oku = async (caseId, belgeId = null) => {
    if (!caseId) return;
    setOkuma({ durum: 'okunuyor' });
    try {
      const s = await aracBilgiAPI.ruhsatOku(caseId, belgeId);
      await sonucuIsle(s);
    } catch (e) {
      setOkuma({ durum: 'hata', mesaj: 'Ruhsat okunamadı: ' + (e?.response?.data?.message || e?.message || '') });
    }
  };

  const yukle = async (dosya) => {
    if (!dosya) return;
    setOkuma({ durum: 'okunuyor' });
    try {
      const s = await aracBilgiAPI.ruhsatOkuYuklenen(dosya);
      await sonucuIsle({ ...s, adaylar: adaylar }, dosya.name);
    } catch (e) {
      setOkuma({ durum: 'hata', mesaj: 'Fotoğraf okunamadı: ' + (e?.response?.data?.message || e?.message || '') });
    } finally {
      if (dosyaGirdisi.current) dosyaGirdisi.current.value = '';
    }
  };

  const dosyaSec = (d) => {
    setListeAcik(false);
    setAra('');
    setOkuma(null);
    setAdaylar([]);
    onDosyaSec(d);
    oku(alanAl(d, 'Id'));
  };

  const yukleDugmesi = (
    <label className="ah-btn ah-btn-kucuk ah-yukle">
      Ruhsat fotoğrafı yükle
      <input ref={dosyaGirdisi} type="file" accept="image/*,application/pdf" hidden
        onChange={(e) => yukle(e.target.files?.[0])} />
    </label>
  );

  return (
    <section className="ah-kart ah-dosya">
      <h3 className="ah-baslik"><span className="ah-no ah-no-altin">✦</span>{embedded ? 'Ruhsat ve ekspertizden doldur' : 'Dosyadan, ruhsattan ve ekspertizden doldur'}</h3>

      {!embedded && !seciliDosya && (
        <div className="ah-dosya-ara">
          <input className="ah-inp" value={ara} placeholder="Föy no, müvekkil adı ya da plaka"
            aria-label="Dosya ara"
            onFocus={() => { dosyalariGetir(); setListeAcik(true); }}
            // Listeye dokunma, kutudan çıkıştan önce işlensin diye kapanma biraz bekler.
            onBlur={() => setTimeout(() => setListeAcik(false), 200)}
            onChange={(e) => { setAra(e.target.value); setListeAcik(true); dosyalariGetir(); }} />
          {listeAcik && (
            <ul className="ah-dosya-liste" role="listbox">
              {dosyalar === null && <li className="ah-dosya-bos">Dosyalar yükleniyor…</li>}
              {dosyalar !== null && sonuclar.length === 0 && <li className="ah-dosya-bos">Eşleşen dosya yok.</li>}
              {sonuclar.map((d) => (
                <li key={alanAl(d, 'Id')}>
                  <button type="button" className="ah-dosya-satir" onClick={() => dosyaSec(d)}>
                    <b>{alanAl(d, 'FoyNo')}</b>
                    <span>{alanAl(d, 'Muvekkil')}</span>
                    {alanAl(d, 'MuvekkilPlaka') && <small>{alanAl(d, 'MuvekkilPlaka')}</small>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="ah-not">Dosyayı seçince plaka, kaza ve sigorta bilgileri gelir; dosyadaki ruhsattan marka, model ve yıl okunur.</p>
        </div>
      )}

      {!embedded && seciliDosya && (
        <div className="ah-dosya-secili">
          <span>Föy <b>{seciliDosya.foyNo}</b> · {seciliDosya.muvekkil}</span>
          <button type="button" className="ah-bag" onClick={() => { setOkuma(null); setAdaylar([]); onDosyaBirak(); }}>Değiştir</button>
        </div>
      )}

      <div className="ah-ruhsat">
        {(embedded || seciliDosya) && okuma?.durum !== 'okunuyor' && (
          <button type="button" className="ah-btn ah-btn-kucuk ah-btn-koyu" onClick={() => oku(kimlik)}>
            {okuma ? 'Ruhsatı yeniden oku' : 'Dosyadaki ruhsattan doldur'}
          </button>
        )}
        {(embedded || seciliDosya) && okuma?.durum !== 'okunuyor' && yukleDugmesi}

        {okuma?.durum === 'okunuyor' && (
          <p className="ah-okunuyor" role="status"><span className="ah-donen" aria-hidden="true" /> Ruhsat okunuyor… (10–30 sn)</p>
        )}
        {okuma?.durum === 'tamam' && (
          <div className="ah-ruhsat-sonuc" role="status">
            <b>Ruhsattan dolduruldu:</b> {okuma.ozet || '—'}
            {okuma.eksik && !paketSecili && <div className="ah-uyari-metin">{okuma.eksik}</div>}
            <div className="ah-alt">Okunan belge: {okuma.belgeAdi}. Lütfen kontrol edin; okuma hatalı olabilir.</div>
          </div>
        )}
        {okuma?.durum === 'hata' && (
          <div className="ah-ruhsat-hata" role="alert">{okuma.mesaj}</div>
        )}

        {adaylar.length > 1 && okuma?.durum !== 'okunuyor' && (
          <label className="ah-alan ah-ruhsat-aday"><span>Dosyada birden çok ruhsat var — okunacak belge</span>
            <select className="ah-inp" value={okuma?.belgeId || ''} onChange={(e) => oku(kimlik, Number(e.target.value))}>
              {!okuma?.belgeId && <option value="">Seçin</option>}
              {adaylar.map((a) => (
                <option key={a.id} value={a.id}>{a.ad}{a.karsiTaraf ? ' (karşı taraf?)' : ''}</option>
              ))}
            </select></label>
        )}
      </div>

      {onEkspertiz && <EkspertizBolumu dosyaKimligi={kimlik} onEkspertiz={onEkspertiz} />}
    </section>
  );
};

export default DosyaRuhsat;
